import { GERMAN_STATE_LABELS } from '#lib/constants/germanStates';

const WEBSITE_FILTER_LABELS = {
	all: 'Alle Firmen',
	with: 'Nur mit Website',
	without: 'Nur ohne Website',
};

const LEGAL_FORM_LABELS = {
	gmbh: 'GmbH',
	ag: 'AG',
	ug: 'UG',
	kg: 'KG',
	ohg: 'OHG',
	eg: 'eG',
	ev: 'e.V.',
	gbr: 'GbR',
	ggmbh: 'gGmbH',
	kgaa: 'KGaA',
	se: 'SE',
	ek: 'Einzelkaufmann',
	ewiv: 'EWIV',
	llp: 'LLP',
	foreign: 'Ausländische Gesellschaft',
	municipal: 'Kommunale Einrichtung',
	unknown: 'Unbekannt',
};

function formatRange(from, to, unit = '') {
	const suffix = unit ? ` ${unit}` : '';
	if (from !== null && from !== undefined && to !== null && to !== undefined) return `${from} – ${to}${suffix}`;
	if (from !== null && from !== undefined) return `ab ${from}${suffix}`;
	if (to !== null && to !== undefined) return `bis ${to}${suffix}`;
	return null;
}

function formatEurRange(min, max) {
	const fmt = (v) => {
		if (v === null || v === undefined) return null;
		if (v >= 1000000) return `${(v / 1000000).toFixed(1)} Mio.`;
		if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
		return `${v}`;
	};
	const fmtMin = fmt(min);
	const fmtMax = fmt(max);
	if (fmtMin && fmtMax) return `${fmtMin} – ${fmtMax} €`;
	if (fmtMin) return `ab ${fmtMin} €`;
	if (fmtMax) return `bis ${fmtMax} €`;
	return null;
}

function formatLegalForms(forms) {
	if (!forms) return null;
	const arr = Array.isArray(forms) ? forms : [forms];
	if (arr.length === 0) return null;
	const labels = arr.map((f) => LEGAL_FORM_LABELS[f] || f);
	return { label: 'Rechtsform', value: labels, type: 'tags' };
}

/**
 * Converts a search query input object into human-readable entries.
 * Supports various query types (Maps, HR, CSV, Anymail) with type-specific formatting.
 * For unknown query types, all fields are output generically.
 * @param {Object} input - Raw input JSON data of the search query
 * @param {string} queryTypeCode - Query type code (e.g. 'b2bheroLeadSearch', 'b2bheroCommercialRegisterSearch', 'csvImport')
 * @returns {Array<{label: string, value: string|string[]|boolean, type: 'text'|'tags'|'boolean'}>}
 */
export function formatQueryInput(input, queryTypeCode) {
	if (!input || typeof input !== 'object') return [];

	const entries = [];

	switch (queryTypeCode) {
		case 'b2bheroLeadSearch': {
			if (input.searchTerms?.length) entries.push({ label: 'Branchen', value: input.searchTerms, type: 'tags' });
			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.postalCodeFrom && input.postalCodeTo)
				entries.push({
					label: 'PLZ-Bereich',
					value: `${input.postalCodeFrom} – ${input.postalCodeTo}`,
					type: 'text',
				});
			if (input.postalCodes) entries.push({ label: 'PLZ-Liste', value: input.postalCodes, type: 'text' });
			if (input.states?.length)
				entries.push({
					label: 'Bundesländer',
					value: input.states.map((s) => GERMAN_STATE_LABELS[s] || s),
					type: 'tags',
				});
			if (input.radius) entries.push({ label: 'Radius', value: `${input.radius} km`, type: 'text' });
			if (input.maxResults)
				entries.push({ label: 'Max. Ergebnisse', value: String(input.maxResults), type: 'text' });
			if (input.country) entries.push({ label: 'Land', value: input.country, type: 'text' });
			if (WEBSITE_FILTER_LABELS[input.websiteFilter])
				entries.push({ label: 'Website', value: WEBSITE_FILTER_LABELS[input.websiteFilter], type: 'text' });
			if (input.ratingMin || input.ratingMax) {
				const ratingRange = formatRange(input.ratingMin, input.ratingMax, '★');
				if (ratingRange) entries.push({ label: 'Bewertung', value: ratingRange, type: 'text' });
			}
			if (input.minReviewCount)
				entries.push({ label: 'Min. Bewertungen', value: `≥ ${input.minReviewCount}`, type: 'text' });
			if (input.skipClosedPlaces === false)
				entries.push({ label: 'Geschlossene Unternehmen einbezogen', value: true, type: 'boolean' });
			if (input.nameMatch === 'only_includes')
				entries.push({ label: 'Nur Orte mit Suchbegriff im Namen', value: true, type: 'boolean' });
			if (input.excludeKeywords?.length > 0)
				entries.push({ label: 'Ausschlussbegriffe', value: input.excludeKeywords, type: 'tags' });
			break;
		}

		case 'b2bheroCommercialRegisterSearch': {
			// New unified API format + backward compat with old format + agentic search format
			if (input.industrySearchTerms?.length)
				entries.push({ label: 'Branchen', value: input.industrySearchTerms, type: 'tags' });
			else if (input.industryCodes?.length)
				entries.push({ label: 'Branchen', value: input.industryCodes, type: 'tags' });
			else if (input.keyword) entries.push({ label: 'Stichwort', value: input.keyword, type: 'text' });

			const lf = formatLegalForms(input.legalForm || input.companyLegalForm);
			if (lf) entries.push(lf);

			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.postalCodeFrom && input.postalCodeTo)
				entries.push({
					label: 'PLZ-Bereich',
					value: `${input.postalCodeFrom} – ${input.postalCodeTo}`,
					type: 'text',
				});
			if (input.postalCodes) entries.push({ label: 'PLZ-Liste', value: input.postalCodes, type: 'text' });
			if (input.states?.length)
				entries.push({
					label: 'Bundesländer',
					value: input.states.map((s) => GERMAN_STATE_LABELS[s] || s),
					type: 'tags',
				});
			if (input.country && input.country !== 'Deutschland')
				entries.push({ label: 'Land', value: input.country, type: 'text' });
			if (input.radius) entries.push({ label: 'Radius', value: `${input.radius} km`, type: 'text' });

			// New format (EUR absolute)
			const rev = formatEurRange(input.revenueMin, input.revenueMax);
			// Old format (Mio. EUR)
			const revOld = !rev ? formatEurRange(input.companyMinRevenue, input.companyMaxRevenue) : null;
			if (rev) entries.push({ label: 'Umsatz', value: rev, type: 'text' });
			else if (revOld) entries.push({ label: 'Umsatz', value: revOld, type: 'text' });

			const bal = formatEurRange(input.balanceTotalMin, input.balanceTotalMax);
			const balOld = !bal
				? formatEurRange(input.companyMinBalanceSheetTotal, input.companyMaxBalanceSheetTotal)
				: null;
			if (bal) entries.push({ label: 'Bilanzsumme', value: bal, type: 'text' });
			else if (balOld) entries.push({ label: 'Bilanzsumme', value: balOld, type: 'text' });

			const earn = formatEurRange(input.earningsMin, input.earningsMax);
			if (earn) entries.push({ label: 'Gewinn', value: earn, type: 'text' });

			const emp =
				formatRange(input.employeesMin, input.employeesMax) ||
				formatRange(input.companyMinEmployees, input.companyMaxEmployees);
			if (emp) entries.push({ label: 'Mitarbeiter', value: emp, type: 'text' });

			if (input.maxResults)
				entries.push({ label: 'Max. Ergebnisse', value: String(input.maxResults), type: 'text' });
			if (input.eventType) entries.push({ label: 'Ereignis', value: input.eventType, type: 'text' });
			if (input.includeEstimated === false)
				entries.push({ label: 'Nur bestätigte Werte', value: true, type: 'boolean' });
			if (input.searchKeywords?.length > 0)
				entries.push({ label: 'Stichwörter', value: input.searchKeywords, type: 'tags' });
			if (input.derivedMetrics?.length > 0)
				entries.push({
					label: 'Erweiterte Filter',
					value: `${input.derivedMetrics.length} aktiv`,
					type: 'text',
				});
			if (input.excludeKeywords?.length > 0)
				entries.push({ label: 'Ausschlussbegriffe', value: input.excludeKeywords, type: 'tags' });
			break;
		}

		case 'csvImport': {
			entries.push({ label: 'Quelle', value: 'CSV Import', type: 'text' });
			if (input.fileName) entries.push({ label: 'Datei', value: input.fileName, type: 'text' });
			break;
		}

		case 'anymailfinderCompanySearch': {
			if (input.keyword) entries.push({ label: 'Suchbegriff', value: input.keyword, type: 'text' });
			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.domain) entries.push({ label: 'Domain', value: input.domain, type: 'text' });
			break;
		}

		case 'anymailfinderPeopleSearch':
		case 'anymailfinderDecisionmakerSearch': {
			if (input.keyword) entries.push({ label: 'Suchbegriff', value: input.keyword, type: 'text' });
			if (input.location) entries.push({ label: 'Standort', value: input.location, type: 'text' });
			if (input.jobTitle) entries.push({ label: 'Jobtitel', value: input.jobTitle, type: 'text' });
			if (input.companyName) entries.push({ label: 'Firma', value: input.companyName, type: 'text' });
			break;
		}

		case 'anymailfinderLinkedinSearch': {
			if (input.linkedinUrl) entries.push({ label: 'LinkedIn URL', value: input.linkedinUrl, type: 'text' });
			if (input.keyword) entries.push({ label: 'Suchbegriff', value: input.keyword, type: 'text' });
			break;
		}

		default: {
			for (const [key, value] of Object.entries(input)) {
				if (value === null || value === undefined || value === '') continue;
				if (typeof value === 'boolean') {
					entries.push({ label: key, value, type: 'boolean' });
				} else if (Array.isArray(value)) {
					entries.push({ label: key, value: value.map(String), type: 'tags' });
				} else {
					entries.push({ label: key, value: String(value), type: 'text' });
				}
			}
		}
	}

	return entries;
}

/**
 * Formats search query inputs as plain text for native title attributes.
 * @param {Object} input - Raw input JSON data of the search query
 * @param {string} queryTypeCode - Query type code
 * @returns {string} Line-by-line formatted text or empty string if no inputs
 */
export function formatQueryInputPlainText(input, queryTypeCode) {
	const entries = formatQueryInput(input, queryTypeCode);
	if (entries.length === 0) return '';

	return entries
		.map((e) => {
			if (e.type === 'boolean') return `${e.label}: ${e.value ? 'Ja' : 'Nein'}`;
			if (e.type === 'tags') return `${e.label}: ${Array.isArray(e.value) ? e.value.join(', ') : e.value}`;
			return `${e.label}: ${e.value}`;
		})
		.join('\n');
}
